import {
  getGetSettingsBasicQueryKey,
  getGetSettingsGeneralQueryKey,
  getGetSettingsSystemQueryKey,
  useGetSettingsBasic,
  useGetSettingsGeneral,
  useGetSettingsSystem,
  usePutSettingsBasic,
  usePutSettingsGeneral,
  usePutSettingsSystem,
} from '@agos/api-client/platform'
import { T, formatCount, tt, useAuth } from '@agos/shared'
import {
  ConfirmDialog,
  PageHeader,
  Spinner,
  StatusBadge,
  cn,
  useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Check,
  Clock,
  Code,
  Megaphone,
  Save,
  Settings,
  ShieldAlert,
  Store,
  X,
} from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason, saveMessage, saveOutcome, settled } from '../write-access'
import {
  KITCHEN_INTERVAL_MAX,
  KITCHEN_INTERVAL_MIN,
  LANGUAGES,
  PHONE_NUMBER_MAX,
  TIMEZONES,
  basicPayload,
  carriesCredential,
  closesShop,
  flagLabel,
  formOf,
  generalPayload,
  injectsScript,
  kitchenIntervalDrift,
  severityOf,
  shopState,
  shopStateMessage,
  staleFlags,
  systemPayload,
  COMING_SOON,
  MOVED_ELSEWHERE,
  type GeneralForm,
  type GeneralSection,
  type SystemSection,
} from './general-settings'

/**
 * `/settings-general` — one screen over three settings sections.
 *
 * **FB-130.** Legacy fires three PUTs and shows a green tick regardless:
 *
 * ```js
 * await Promise.all([fetch(…general), fetch(…system), fetch(…basic)])
 * setSaved(true)
 * ```
 *
 * `fetch` resolves on a 403 as happily as on a 200 and there is no `res.ok` anywhere on the page, so
 * three refusals show as three successes. The PUTs are `main_admin` only while the GETs admit `admin`
 * (FB-001), so for an admin the tick is *always* false. And three independent writes can half-succeed,
 * which one success message cannot express.
 *
 * **FB-131.** Three flags are stored twice — as columns, which `RestaurantAvailability` reads when it
 * decides whether an order may be placed, and inside `system_settings_json`, which the Control Tower
 * reads. Legacy writes only the columns, so the mirror drifts and a super admin can see a stale
 * maintenance flag. Both are written here, and a drift found on load is named.
 *
 * **FB-132.** `GET /settings/basic` hands over a live Anthropic API key to any `admin`, and this page
 * needs four of that section's sixteen fields. The four are taken by name so the credential never
 * enters this page's state, and the save sends four keys into a merging endpoint rather than echoing
 * the section back.
 *
 * Ported from legacy `pages/settings-general.tsx` (440 lines).
 */

export function SettingsGeneralPage() {
  const general = useGetSettingsGeneral()
  const system = useGetSettingsSystem()
  const basic = useGetSettingsBasic()

  if (general.isLoading || system.isLoading || basic.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <GeneralSettingsForm general={general.data} system={system.data} basic={basic.data} />
  )
}

function GeneralSettingsForm({
  general,
  system,
  basic,
}: {
  general: GeneralSection | undefined
  system: SystemSection | undefined
  basic: Record<string, unknown> | undefined
}) {
  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [saved] = useState(() => formOf(general, system, basic))
  const [form, setForm] = useState<GeneralForm>(saved)
  const [confirmClose, setConfirmClose] = useState<GeneralForm | null>(null)
  // §9.285 — the list of what left this page and where the four that shadow a screen actually live.
  const [comingSoonOpen, setComingSoonOpen] = useState(false)

  const writable = mayWrite('general', user?.systemRole)
  const readOnly = readOnlyReason('general', user?.systemRole)
  const drifted = staleFlags(general, system)
  const intervalDrift = kitchenIntervalDrift(general, basic)
  const state = shopState(form)

  const putGeneral = usePutSettingsGeneral()
  const putSystem = usePutSettingsSystem()
  const putBasic = usePutSettingsBasic()
  const saving = putGeneral.isPending || putSystem.isPending || putBasic.isPending

  const set = <K extends keyof GeneralForm>(key: K, value: GeneralForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  /**
   * Three writes, each reported on its own.
   *
   * `Promise.allSettled`, not `all`: one refusal must not hide whether the other two landed. The
   * generated client throws on a non-2xx, so a rejection here is a real refusal and not a guess.
   */
  const save = async (next: GeneralForm) => {
    const results = await Promise.allSettled([
      // Spread into a literal: the generated bodies are `{[key: string]: unknown}` because the spec
      // does not describe them, and a named interface is not assignable to an index signature. The
      // precise types stay, so a test can still assert the exact keys.
      putGeneral.mutateAsync({ data: { ...generalPayload(next) } }),
      putSystem.mutateAsync({ data: { ...systemPayload(next) } }),
      putBasic.mutateAsync({ data: { ...basicPayload(next) } }),
    ])
    // §9.392 - the `reason` comes with the result, and reducing it to a boolean here is what threw
    // the server's own sentence away on all three writes.
    const outcome = saveOutcome([
      { section: tt('všeobecné'), ...settled(results[0]) },
      { section: tt('systémové'), ...settled(results[1]) },
      { section: tt('rozšírené'), ...settled(results[2]) },
    ])
    const message = saveMessage(outcome)
    toast({
      title: message.title,
      description: message.description,
      variant: outcome.state === 'all' ? 'default' : 'destructive',
    })
    for (const key of [
      getGetSettingsGeneralQueryKey(),
      getGetSettingsSystemQueryKey(),
      getGetSettingsBasicQueryKey(),
    ]) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const onSave = () => {
    // Closing the shop is not something to discover afterwards.
    if (closesShop(saved, form)) {
      setConfirmClose(form)
      return
    }
    void save(form)
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Všeobecné nastavenia')}
        description={tt('Prevádzka, zákaznícka stránka a systém')}
        icon={<Settings className="w-5 h-5" />}
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {/* FB-130 + FB-001: legacy lets an admin press save and shows a green tick on three 403s. */}
          {readOnly}
        </p>
      )}

      {/* The two switches that stop the restaurant taking money, said once and plainly. */}
      <div
        className={cn(
          'rounded-2xl border px-4 py-3 flex items-center gap-3',
          state.takingOrders ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-red-500/40 bg-red-500/5',
        )}
        data-shop-state={state.reason}
      >
        {state.takingOrders ? (
          <Check className="w-5 h-5 text-emerald-500 shrink-0" />
        ) : (
          <ShieldAlert className="w-5 h-5 text-red-500 shrink-0" />
        )}
        <p className={cn('text-sm font-semibold', state.takingOrders ? 'text-emerald-500' : 'text-red-500')}>
          {shopStateMessage(state)}
        </p>
      </div>

      {drifted.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-xs space-y-1" data-flag-drift>
          {/* FB-131. The columns decide whether orders are taken; the blob is what the Control Tower
              shows. Legacy writes only the columns, so the blob goes stale and nobody is told. */}
          <p className="font-bold text-amber-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <T>Dve uložené kópie tých istých prepínačov sa rozišli</T>
          </p>
          {drifted.map((copy) => (
            <p key={copy.flag} className="text-muted-foreground" data-drifted={copy.flag}>
              <strong>{flagLabel(copy.flag)}</strong>: <T>platí</T>{' '}
              {copy.authoritative ? <T>zapnuté</T> : <T>vypnuté</T>},{' '}
              <T>v systémovom nastavení je ale</T>{' '}
              {copy.mirror ? <T>zapnuté</T> : <T>vypnuté</T>}
            </p>
          ))}
          <p className="text-muted-foreground">
            <T>
              Objednávky sa riadia prvou hodnotou. Druhú vidí super administrátor v Control Tower -
              uložením tejto stránky sa zrovnajú.
            </T>
          </p>
        </div>
      )}

      {intervalDrift !== null && (
        <p className="text-[11px] text-amber-500" data-interval-drift>
          <T>Interval kuchynskej obrazovky je uložený dvakrát</T>:{' '}
          {formatCount(intervalDrift.general)} <T>a</T> {formatCount(intervalDrift.basic)}{' '}
          <T>sekúnd. Používa sa druhá hodnota; uložením sa zrovnajú.</T>
        </p>
      )}

      {/* FB-132 - a tripwire, not a notice.
        *
        * The endpoint stopped sending `anthropicApiKey` on 2026-08-02; it answers
        * `anthropicApiKeySet`, a boolean. So this renders nothing today, and that is the point: if the
        * key ever comes back into this payload, the screen says so out loud instead of quietly
        * carrying it again. The value is still never rendered — only the fact that it arrived. */}
      {carriesCredential(basic) && (
        <p className="text-[11px] text-amber-500" data-credential-warning>
          <T>
            Pozor: odpoveď nastavení znovu obsahuje API kľúč pre preklady. Táto stránka ho nezobrazuje
            ani neposiela ďalej, ale je viditeľný v sieťovej odpovedi každému administrátorovi - treba
            to nahlásiť.
          </T>
        </p>
      )}

      <Section title={tt('Prevádzka')} icon={<Store className="w-4 h-4 text-primary" />}>
        <Switch
          field="maintenanceMode"
          label={tt('Režim údržby')}
          description={tt('Zákaznícka stránka sa nedá použiť a objednávky sa neprijímajú.')}
          form={form}
          disabled={!writable}
          onToggle={set}
        />
        <Switch
          field="onlineOrdersEnabled"
          label={tt('Online objednávky')}
          description={tt('Keď je vypnuté, zákazník si neobjedná ani keď je stránka dostupná.')}
          form={form}
          disabled={!writable}
          onToggle={set}
        />
        <Switch
          field="autoAccept"
          label={tt('Automaticky potvrdzovať objednávky')}
          description={tt('Nová objednávka nečaká na potvrdenie v dispečingu.')}
          form={form}
          disabled={!writable}
          onToggle={set}
        />
        <Field label={tt('Telefón na stránke')} htmlFor="general-phone">
          <input
            id="general-phone"
            className={FIELD}
            maxLength={PHONE_NUMBER_MAX}
            disabled={!writable}
            // The restaurant's own number, shown on the customer site. `maxLength` is the server's,
            // so the field keeps its own input rather than taking the shared one.
            inputMode="tel"
            autoComplete="tel"
            value={form.phoneNumber}
            onChange={(event) => set('phoneNumber', event.target.value)}
          />
        </Field>
        <Field label={tt('Adresa v pätičke')} htmlFor="general-footer">
          <input
            id="general-footer"
            className={FIELD}
            disabled={!writable}
            value={form.footerAddress}
            onChange={(event) => set('footerAddress', event.target.value)}
          />
        </Field>
      </Section>

      <Section title={tt('Oznam pre zákazníkov')} icon={<Megaphone className="w-4 h-4 text-primary" />}>
        <Field label={tt('Text oznamu')} htmlFor="general-announcement">
          <textarea
            id="general-announcement"
            className={cn(FIELD, 'resize-none min-h-[72px]')}
            disabled={!writable}
            value={form.announcement}
            onChange={(event) => set('announcement', event.target.value)}
          />
          <p className="text-[11px] text-muted-foreground" data-announcement-state>
            {/* Legacy's own rule, made visible: text in the box is what switches the banner on. */}
            {form.announcement.trim() === '' ? (
              <T>Prázdny oznam sa zákazníkom nezobrazí.</T>
            ) : (
              <T>Tento oznam sa zobrazí na zákazníckej stránke.</T>
            )}
          </p>
        </Field>
      </Section>

      <Section title={tt('Systém')} icon={<Settings className="w-4 h-4 text-primary" />}>
        <Field label={tt('Časová zóna')} htmlFor="general-timezone">
          <select
            id="general-timezone"
            className={FIELD}
            disabled={!writable}
            value={form.timezone}
            onChange={(event) => set('timezone', event.target.value)}
          >
            {TIMEZONES.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
        </Field>
        <Field label={tt('Jazyk')} htmlFor="general-language">
          <select
            id="general-language"
            className={FIELD}
            disabled={!writable}
            value={form.language}
            onChange={(event) => set('language', event.target.value)}
          >
            {LANGUAGES.map((language) => (
              <option key={language.code} value={language.code}>
                {language.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={tt('Interval kuchynskej obrazovky (s)')} htmlFor="general-interval">
          <input
            id="general-interval"
            type="number"
            min={KITCHEN_INTERVAL_MIN}
            max={KITCHEN_INTERVAL_MAX}
            className={FIELD}
            disabled={!writable}
            value={form.kitchenDisplayInterval}
            onChange={(event) => set('kitchenDisplayInterval', Number(event.target.value))}
          />
        </Field>
        <Field label={tt('Zvýraznené telefónne čísla')} htmlFor="general-highlighted">
          <input
            id="general-highlighted"
            className={FIELD}
            disabled={!writable}
            value={form.highlightedPhones}
            onChange={(event) => set('highlightedPhones', event.target.value)}
          />
        </Field>
      </Section>

      <Section title={tt('Vlastný kód')} icon={<Code className="w-4 h-4 text-primary" />}>
        {(
          [
            { field: 'customHeaderCode' as const, label: tt('Kód v hlavičke') },
            { field: 'customFooterCode' as const, label: tt('Kód v pätičke') },
          ]
        ).map((entry) => (
          <Field key={entry.field} label={entry.label} htmlFor={`general-${entry.field}`}>
            <textarea
              id={`general-${entry.field}`}
              className={cn(FIELD, 'resize-none min-h-[72px] font-mono text-xs')}
              disabled={!writable}
              value={form[entry.field]}
              onChange={(event) => set(entry.field, event.target.value)}
            />
            {injectsScript(form[entry.field]) && (
              <p className="text-[11px] text-amber-500" data-script-warning={entry.field}>
                <T>
                  Tento kód obsahuje `script` a vloží sa do zákazníckej stránky. Beží pred zákazníkmi.
                </T>
              </p>
            )}
          </Field>
        ))}
      </Section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-save
          disabled={!writable || saving}
          onClick={onSave}
          className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {saving ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť nastavenia</T>
        </button>
        {/* §9.285 - twelve switches were removed from this page. Without this list their removal
          * reads as a feature having been taken away, and the four that shadow a working screen
          * would leave nobody knowing where the switch that counts actually is. */}
        <button
          type="button"
          data-coming-soon-open
          onClick={() => setComingSoonOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold hover:bg-muted/50 transition-colors"
        >
          <Clock className="w-4 h-4" /> <T>Pripravujeme</T>
        </button>
      </div>

      {comingSoonOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div
            className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
            data-coming-soon-dialog
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-bold">
                <T>Pripravujeme</T>
              </h2>
              <button
                type="button"
                aria-label={tt('Zatvoriť')}
                onClick={() => setComingSoonOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto">
              <p className="text-xs text-muted-foreground">
                <T>
                  Tieto prepínače boli z tejto stránky odstránené, lebo nič nerobili. Uložené hodnoty
                  ostávajú - keď funkciu doprogramujeme, prepínač sa vráti.
                </T>
              </p>
              <ul className="space-y-3">
                {COMING_SOON.map((entry) => (
                  <li key={entry.key} className="space-y-0.5" data-coming-soon={entry.key}>
                    <p className="text-sm font-semibold">{entry.label}</p>
                    <p className="text-xs text-muted-foreground">{entry.description}</p>
                  </li>
                ))}
              </ul>

              <div className="pt-4 border-t border-border space-y-3">
                <p className="text-xs text-muted-foreground">
                  <T>
                    Tieto sa nastavujú inde - prepínač na tejto stránke ich neovládal, takže vypnutie
                    tu nič nezmenilo.
                  </T>
                </p>
                <ul className="space-y-2">
                  {MOVED_ELSEWHERE.map((entry) => (
                    <li
                      key={entry.key}
                      className="text-sm flex flex-wrap items-baseline gap-x-2"
                      data-moved-elsewhere={entry.key}
                    >
                      <span className="font-semibold">{entry.label}</span>
                      <span className="text-xs text-muted-foreground">{entry.where}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmClose !== null}
        title={tt('Zavrieť objednávky?')}
        description={
          confirmClose === null
            ? ''
            : `${shopStateMessage(shopState(confirmClose))} ${tt('Zmena platí okamžite.')}`
        }
        confirmLabel={tt('Zavrieť objednávky')}
        variant="danger"
        loading={saving}
        onClose={() => setConfirmClose(null)}
        onConfirm={() => {
          const next = confirmClose
          setConfirmClose(null)
          if (next !== null) void save(next)
        }}
      />
    </div>
  )
}

const FIELD =
  'px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 w-full disabled:opacity-60'

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        {icon}
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</p>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6">
      <label className="text-sm font-semibold sm:w-64 shrink-0 pt-1.5" htmlFor={htmlFor}>
        {label}
      </label>
      <div className="flex-1 space-y-1.5 min-w-0">{children}</div>
    </div>
  )
}

/** A switch, with the two that close the shop marked as such. */
function Switch<K extends keyof GeneralForm>({
  field,
  label,
  description,
  form,
  disabled,
  onToggle,
}: {
  field: K
  label: string
  description: string
  form: GeneralForm
  disabled: boolean
  onToggle: (field: K, value: GeneralForm[K]) => void
}) {
  const value = form[field] === true
  const closes = severityOf(field) === 'closes-shop'

  return (
    <div className="px-5 py-4 flex items-center gap-4" data-switch={field}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold">{label}</p>
          {closes && (
            // Legacy draws all twenty-three switches identically, so the two that stop the restaurant
            // taking money look like the one that shows calories.
            <StatusBadge label={tt('ovplyvňuje objednávky')} color="amber" />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        data-switch-state={value ? 'on' : 'off'}
        onClick={() => onToggle(field, !value as GeneralForm[K])}
        className={cn(
          'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
          value ? 'bg-primary' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
            value ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}
